import * as THREE from "three";
import Experience from "../Experience.js";
import settings from "../settings.js";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib";

export default class Environment {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.debug = this.experience.debug;

    // Debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder("environment");
      this.debugFolder.close();
    }

    this.ambientLight = new THREE.AmbientLight(0xfff5e1, 0.1);
    this.scene.add(this.ambientLight);

    this.lampLight = this.setPointLight("lamp", settings.lights.lamp);
    this.nightLight = this.setPointLight("night", settings.lights.night);
    this.pictureLight = this.setPointLight("picture", settings.lights.picture);
    this.setPCLight();

    // Starting intensities, which scroll lighting scales from
    this.base = {
      ambient: this.ambientLight.intensity,
      night: this.nightLight?.intensity,
      picture: this.pictureLight?.intensity,
    };

    this.setEnvironmentMap();
  }

  // Evening falls as the page scrolls: the fill light dims, the night light
  // grows, and glowing materials dim to match. See settings.scrollLighting.
  scroll() {
    const config = settings.scrollLighting;
    if (!config) return;

    const max = document.documentElement.scrollHeight - window.innerHeight;
    const progress =
      max > 0 ? THREE.MathUtils.clamp(window.scrollY / max, 0, 1) : 0;
    const blend = (end) => THREE.MathUtils.lerp(1, end, progress);

    const fill = blend(config.fill);
    this.ambientLight.intensity = this.base.ambient * fill;
    if (this.pictureLight)
      this.pictureLight.intensity = this.base.picture * fill;
    this.setEnvMapIntensity(this.environmentMap.intensity * fill);
    if (this.nightLight) {
      this.nightLight.intensity = this.base.night * blend(config.night);
    }

    for (const { material, base, end } of this.glowMaterials()) {
      material.emissiveIntensity = base * blend(end);
    }
  }

  // Emissive materials named in settings.scrollLighting.glow, with their
  // original intensity. Found once the room model is in the scene.
  glowMaterials() {
    if (this.glow) return this.glow;
    if (!this.experience.world?.room) return [];

    const names = settings.scrollLighting.glow;
    const found = new Map();
    this.scene.traverse((child) => {
      const material = child.material;
      if (child.isMesh && material && material.name in names) {
        found.set(material, {
          material,
          base: material.emissiveIntensity,
          end: names[material.name],
        });
      }
    });
    this.glow = [...found.values()];
    return this.glow;
  }

  // Intensity is a uniform, so no needsUpdate (which would recompile shaders)
  setEnvMapIntensity(value) {
    this.scene.traverse((child) => {
      if (child.isMesh && child.material?.envMap) {
        child.material.envMapIntensity = value;
      }
    });
  }

  setPointLight(name, config) {
    if (!config) return null;

    const light = new THREE.PointLight(config.color, config.intensity);
    light.position.set(...config.position);
    if (config.castShadow) {
      light.castShadow = true;
      light.shadow.camera.near = 0.1;
      light.shadow.camera.far = 15;
      light.shadow.mapSize.set(1024, 1024);
      light.shadow.normalBias = 0.05;
      light.shadow.radius = 10;
    }
    this.scene.add(light);

    if (this.debug.active) {
      this.debugFolder
        .add(light, "intensity")
        .name(`${name}LightIntensity`)
        .min(0)
        .max(100)
        .step(0.5);

      this.debugFolder
        .add(light.position, "x")
        .name(`${name}LightX`)
        .min(-5)
        .max(5)
        .step(0.001);

      this.debugFolder
        .add(light.position, "y")
        .name(`${name}LightY`)
        .min(-15)
        .max(5)
        .step(0.001);

      this.debugFolder
        .add(light.position, "z")
        .name(`${name}LightZ`)
        .min(-5)
        .max(5)
        .step(0.001);
    }

    return light;
  }

  setPCLight() {
    RectAreaLightUniformsLib.init();
    this.rectLight = new THREE.RectAreaLight(0xffffff, 0, 2.75, 1.75);
    this.rectLight.position.set(2.38, -3.52, -0.82);
    this.rectLight.rotation.set(2.95, -0.02, 0);
    this.scene.add(this.rectLight);

    if (this.debug.active) {
      this.debugFolder
        .add(this.rectLight, "intensity")
        .name("rectLightIntensity")
        .min(0)
        .max(100)
        .step(0.5);

      this.debugFolder
        .add(this.rectLight.position, "x")
        .name("x")
        .min(-10)
        .max(10)
        .step(0.01);
      this.debugFolder
        .add(this.rectLight.position, "y")
        .name("y")
        .min(-10)
        .max(10)
        .step(0.01);
      this.debugFolder
        .add(this.rectLight.position, "z")
        .name("z")
        .min(-10)
        .max(10)
        .step(0.01);
      this.debugFolder
        .add(this.rectLight.rotation, "x")
        .name("rotateX")
        .min(-6.28)
        .max(6.28)
        .step(0.001);
      this.debugFolder
        .add(this.rectLight.rotation, "y")
        .name("rotateY")
        .min(-6.28)
        .max(6.28)
        .step(0.001);
      this.debugFolder
        .add(this.rectLight.rotation, "z")
        .name("rotateZ")
        .min(-6.28)
        .max(6.28)
        .step(0.001);
    }
  }
  setEnvironmentMap() {
    this.environmentMap = {};
    this.environmentMap.intensity = 0.6;
    this.environmentMap.texture = this.resources.items.environmentMapTexture;
    this.environmentMap.texture.colorSpace = THREE.SRGBColorSpace;

    if (this.environmentMap.texture) {
      this.scene.environment = this.environmentMap.texture;

      this.environmentMap.updateMaterials = () => {
        this.scene.traverse((child) => {
          if (
            child instanceof THREE.Mesh &&
            child.material instanceof THREE.MeshStandardMaterial
          ) {
            child.material.envMap = this.environmentMap.texture;
            child.material.envMapIntensity = this.environmentMap.intensity;
            child.material.needsUpdate = true;
          }
        });
        // Re-apply scroll lighting (the page may load part-way down)
        this.scroll();
      };

      this.environmentMap.texture.onUpdate = () => {
        this.environmentMap.updateMaterials();
      };

      if (this.debug.active) {
        this.debugFolder
          .add(this.environmentMap, "intensity")
          .name("envMapIntensity")
          .min(0)
          .max(4)
          .step(0.001)
          .onChange(this.environmentMap.updateMaterials);
      }

      this.environmentMap.updateMaterials();
    } else {
      console.error("Environment map texture is not loaded yet.");
    }
  }
}
